<template>
  <div
    class="dp-shell"
    :class="{ 'is-app': showNavigation, 'is-fill': fillViewport }"
  >
    <DataPortalHeader
      ref="header"
      :show-menu="showNavigation"
      :nav-open="navigationOpen"
      :inert="mobileModalOpen"
      :aria-hidden="mobileModalOpen ? 'true' : undefined"
      @toggle-navigation="toggleNavigation"
    />
    <div v-if="showNavigation" class="dp-workspace">
      <DataPortalSideNav
        :open="navigationOpen"
        :mobile="isMobile"
        :user="auth.user!"
        @close="closeNavigation"
      />
      <div
        class="dp-content"
        :inert="mobileModalOpen"
        :aria-hidden="mobileModalOpen ? 'true' : undefined"
      >
        <main ref="mainContent" class="dp-main" tabindex="-1">
          <RouterView />
        </main>
      </div>
    </div>
    <div v-else class="dp-public-content">
      <main ref="mainContent" class="dp-main" tabindex="-1">
        <RouterView />
      </main>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useDataPortalAuthStore } from '../auth/data-portal-auth.store';
import DataPortalHeader from '../components/DataPortalHeader.vue';
import DataPortalSideNav from '../components/DataPortalSideNav.vue';

const MOBILE_QUERY = '(max-width: 860px)';
const auth = useDataPortalAuthStore();
const route = useRoute();
const header = ref<InstanceType<typeof DataPortalHeader> | null>(null);
const mainContent = ref<HTMLElement | null>(null);
const isMobile = ref(
  typeof window !== 'undefined' && window.matchMedia(MOBILE_QUERY).matches,
);
const desktopOpen = ref(true);
const mobileOpen = ref(false);
let mediaQuery: MediaQueryList | null = null;
let previousBodyOverflow = '';

const showNavigation = computed(
  () => Boolean(auth.user) && route.meta.dataPortalPublic !== true,
);
// Signed-in pages are one viewport high and only the content area scrolls;
// table pages additionally leave the scrolling to the table itself.
const fillViewport = computed(
  () => showNavigation.value && route.meta.fillViewport === true,
);
const navigationOpen = computed(() =>
  isMobile.value ? mobileOpen.value : desktopOpen.value,
);
const mobileModalOpen = computed(
  () => showNavigation.value && isMobile.value && mobileOpen.value,
);

function toggleNavigation(): void {
  if (isMobile.value) mobileOpen.value = !mobileOpen.value;
  else desktopOpen.value = !desktopOpen.value;
}

async function closeNavigation(): Promise<void> {
  if (!isMobile.value) return;
  mobileOpen.value = false;
  await nextTick();
  header.value?.focusMenuToggle();
}

async function handleViewportChange(
  event: MediaQueryListEvent | MediaQueryList,
): Promise<void> {
  const drawerWasOpen = isMobile.value && mobileOpen.value;
  isMobile.value = event.matches;
  mobileOpen.value = false;
  if (drawerWasOpen) {
    await nextTick();
    header.value?.focusMenuToggle();
  }
}

watch(
  () => route.fullPath,
  async () => {
    if (!isMobile.value || !mobileOpen.value) return;
    mobileOpen.value = false;
    await nextTick();
    mainContent.value?.focus();
  },
);

watch(showNavigation, (visible) => {
  if (visible && !isMobile.value) desktopOpen.value = true;
});

watch(
  () => showNavigation.value && isMobile.value && mobileOpen.value,
  (locked) => {
    if (locked) {
      previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = previousBodyOverflow;
    }
  },
);

onMounted(() => {
  mediaQuery = window.matchMedia(MOBILE_QUERY);
  handleViewportChange(mediaQuery);
  mediaQuery.addEventListener('change', handleViewportChange);
});

onBeforeUnmount(() => {
  mediaQuery?.removeEventListener('change', handleViewportChange);
  document.body.style.overflow = previousBodyOverflow;
});
</script>

<style scoped>
.dp-shell {
  --dp-color-navy: #17324d;
  --dp-color-navy-deep: #0b2238;
  --dp-color-orange: #ed6b21;
  --dp-color-teal: #168f87;
  --dp-color-canvas: #f3f6f6;
  --dp-color-surface: #ffffff;
  --dp-color-surface-muted: #eaf0f1;
  --dp-color-border: #d5dfe2;
  --dp-color-text: #193246;
  --dp-color-muted: #637580;
  --dp-color-danger: #b13b2b;
  --dp-color-success: #21775d;
  --dp-font-display: "Avenir Next Condensed", "Trebuchet MS", sans-serif;
  --dp-font-body: "Avenir Next", "Segoe UI", sans-serif;
  --dp-radius-sm: 0.375rem;
  --dp-radius-md: 0.75rem;
  --dp-radius-lg: 1.25rem;
  --dp-shadow-sm: 0 0.25rem 0.875rem rgba(11, 34, 56, 0.08);
  --dp-shadow-md: 0 1rem 2.5rem rgba(11, 34, 56, 0.13);
  min-height: 100vh;
  min-height: 100dvh;
  display: grid;
  grid-template-rows: auto 1fr;
  font-family: var(--dp-font-body);
  background:
    radial-gradient(
      circle at 98% 4%,
      color-mix(in srgb, var(--dp-color-orange) 10%, transparent),
      transparent 34rem
    ),
    radial-gradient(
      circle at 12% 88%,
      color-mix(in srgb, var(--dp-color-teal) 9%, transparent),
      transparent 30rem
    ),
    var(--dp-color-canvas);
  color: var(--dp-color-text);
}
.dp-workspace {
  min-height: 0;
  display: flex;
}
.dp-content,
.dp-public-content {
  min-width: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: 1fr;
  flex: 1;
}
.dp-public-content .dp-main {
  align-self: center;
}
.dp-main {
  width: min(1400px, calc(100% - clamp(3rem, 7vw, 7rem)));
  margin: 0 auto;
  padding: clamp(2.25rem, 4vw, 3.75rem) 0;
}
.dp-main:focus-visible {
  outline: 3px solid var(--dp-color-orange);
  outline-offset: -3px;
}
/* Signed-in pages: one viewport high, header and navigation stay in place and
   only the content area scrolls. */
.dp-shell.is-app {
  height: 100vh;
  height: 100dvh;
  min-height: 0;
}
.dp-shell.is-app .dp-workspace {
  overflow: hidden;
}
.dp-shell.is-app .dp-sidenav {
  overflow-y: auto;
}
.dp-shell.is-app .dp-content {
  min-height: 0;
  overflow-y: auto;
}
/* Table pages: the content area does not scroll, the table does. */
.dp-shell.is-fill .dp-content {
  overflow: hidden;
}
.dp-shell.is-fill .dp-main {
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 1.25rem 0;
}
@media (max-width: 860px) {
  .dp-workspace {
    display: block;
  }
  .dp-shell.is-app .dp-workspace {
    display: flex;
  }
  .dp-shell.is-fill .dp-main {
    padding: 0.75rem 0;
  }
  .dp-main {
    width: min(100% - 1.5rem, 1400px);
    padding: 1.75rem 0;
  }
}
</style>
